import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageCircle, AlertTriangle, Stethoscope, CheckCircle2 } from "lucide-react";
import { conditions, type Condition } from "@/data/conditions";
import { CONDITION_IMAGES } from "@/data/conditionImages";

const SITE = "https://riomedcare.com.br";

const ConditionPageLayout = ({ condition }: { condition: Condition }) => {
  const url = `${SITE}/what-we-treat/${condition.slug}`;

  const handleWhatsApp = () => {
    const phone = "5521998000676";
    const msg = encodeURIComponent(
      `Hello! I'd like to book a consultation with Rio Med Care regarding: ${condition.name}.`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const related = conditions.filter((c) => c.slug !== condition.slug).slice(0, 6);

  return (
    <>
      <Helmet>
        <title>{condition.metaTitle}</title>
        <meta name="description" content={condition.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={condition.metaTitle} />
        <meta property="og:description" content={condition.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE}${CONDITION_IMAGES[condition.slug].src}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: condition.title,
            description: condition.metaDescription,
            url,
            inLanguage: "en",
            about: { "@type": "MedicalCondition", name: condition.name },
             author: { "@type": "Organization", name: "Rio Med Care", url: SITE },
            publisher: { "@type": "Organization", name: "Rio Med Care", url: SITE },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "What We Treat", item: `${SITE}/what-we-treat` },
              { "@type": "ListItem", position: 3, name: condition.name, item: url },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: condition.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      <Header />

      <main className="bg-background">
        <article className="container mx-auto px-6 py-16 max-w-3xl">
          <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
            <Link to="/" className="text-primary hover:underline">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to="/what-we-treat" className="text-primary hover:underline">
              What We Treat
            </Link>
            <span className="mx-2">/</span>
            <span>{condition.name}</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-6 mb-4 leading-tight">
            {condition.title}
          </h1>
          <p className="text-sm text-muted-foreground mb-10">
             Rio Med Care provides consultations in English, by secure video, throughout Brazil. This page is general information and does not replace an individual medical
            evaluation.
          </p>

          <img
            src={CONDITION_IMAGES[condition.slug].src}
            alt={CONDITION_IMAGES[condition.slug].alt}
            width={1200}
            height={800}
            loading="eager"
            className="w-full rounded-lg border border-border mb-10 object-cover"
          />


          <div className="space-y-10 text-foreground leading-relaxed">
            <section>
              {condition.intro.map((paragraph, i) => (
                <p key={i} className={i === 0 ? "text-lg" : "mt-4"}>
                  {paragraph}
                </p>
              ))}
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">Common symptoms</h2>
              <ul className="space-y-2">
                {condition.symptoms.map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-border bg-muted/40 p-6">
              <h2 className="text-2xl font-semibold mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                When to seek medical care urgently
              </h2>
              <ul className="space-y-2 list-disc pl-5">
                {condition.warningSigns.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-muted-foreground">
                In an emergency in Brazil, call <strong>192 (SAMU)</strong> or go to the nearest emergency department.
                Telemedicine is not appropriate for severe or rapidly evolving conditions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3 flex items-center">
                <Stethoscope className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                 What the consultation includes
              </h2>
              <ul className="space-y-2 list-disc pl-5">
                {condition.consultation.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-muted-foreground">
                Prescriptions and medical certificates are issued only when clinically indicated after a live medical
                evaluation. Rio Med Care does not sell or deliver medications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">Frequently asked questions</h2>
              <div className="space-y-6">
                {condition.faqs.map((faq) => (
                  <div key={faq.q}>
                    <h3 className="font-semibold mb-1">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-border p-6">
              <h2 className="text-2xl font-semibold mb-2">Book a consultation in English</h2>
              <p className="text-muted-foreground mb-5">
                Online consultation US$ 50 (available throughout Brazil). Appointments daily, 07:00–22:00 BRT.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={handleWhatsApp}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Book via WhatsApp
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/#pricing">See pricing</Link>
                </Button>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">Other conditions we treat</h2>
              <ul className="grid sm:grid-cols-2 gap-2">
                {related.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/what-we-treat/${c.slug}`} className="text-primary hover:underline">
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                <Link to="/what-we-treat" className="text-primary hover:underline">
                  View all conditions →
                </Link>
              </p>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default ConditionPageLayout;
